import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

function Comments({ postId, user }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [likedComments, setLikedComments] = useState({});
  const [pinnedComment, setPinnedComment] = useState(null);

  const fetchComments = useCallback(async () => {
    try {
      console.log('Fetching comments for post:', postId);
      
      // Get pinned comment info
      const { data: post } = await supabase
        .from('blog_posts')
        .select('pinned_comment_id, author_id')
        .eq('id', postId)
        .single();

      setPinnedComment(post?.pinned_comment_id || null);

      // Get comments with like counts
      const { data, error } = await supabase
        .from('blog_comments')
        .select(`
          *,
          user:users(full_name, profile_image_url),
          replies:blog_comments!parent_id(*, user:users(full_name, profile_image_url)),
          likes:comment_likes(count)
        `)
        .eq('post_id', postId)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching comments:', error);
        throw error;
      }
      
      console.log('Comments fetched successfully:', data);
      setComments(data || []);

      // Check which comments are liked by current user
      if (user) {
        const { data: userLikes } = await supabase
          .from('comment_likes')
          .select('comment_id')
          .eq('user_id', user.id);

        const likedState = {};
        userLikes?.forEach(like => {
          likedState[like.comment_id] = true;
        });
        setLikedComments(likedState);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      
      // Provide specific error messages
      let errorMessage = 'Failed to load comments.';
      
      if (error.code === '42501') {
        errorMessage = 'Permission denied. Cannot read comments.';
      } else if (error.code === '42P01') {
        errorMessage = 'Comments table does not exist. Please run the database setup.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      console.error(errorMessage);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [postId, user]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    try {
      console.log('Submitting comment:', {
        post_id: postId,
        user_id: user.id,
        content: newComment.trim(),
        parent_id: null
      });

      const { data, error } = await supabase
        .from('blog_comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content: newComment.trim(),
          parent_id: null
        })
        .select();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Comment submitted successfully:', data);
      setNewComment('');
      fetchComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to submit comment. Please try again.';
      
      if (error.code === '42501') {
        errorMessage = 'Permission denied. You may not have permission to comment.';
      } else if (error.code === '23503') {
        errorMessage = 'Invalid post or user reference.';
      } else if (error.code === '23505') {
        errorMessage = 'Duplicate comment detected.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) return;

    try {
      const { error } = await supabase
        .from('blog_comments')
        .update({ content: editText.trim() })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setEditingComment(null);
      setEditText('');
      fetchComments();
    } catch (error) {
      console.error('Error editing comment:', error);
      alert('Failed to edit comment. Please try again.');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const { error } = await supabase
        .from('blog_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const handleSubmitReply = async (parentId) => {
    const replyText = replyTexts[parentId];
    if (!replyText || !replyText.trim() || !user) return;

    try {
      const { error } = await supabase
        .from('blog_comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content: replyText.trim(),
          parent_id: parentId
        });

      if (error) throw error;
      
      setReplyingTo(null);
      setReplyTexts(prev => {
        const newState = { ...prev };
        delete newState[parentId];
        return newState;
      });
      fetchComments();
    } catch (error) {
      console.error('Error submitting reply:', error);
      alert('Failed to submit reply. Please try again.');
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!user) return;

    try {
      const isLiked = likedComments[commentId];
      
      if (isLiked) {
        // Unlike
        const { error } = await supabase
          .from('comment_likes')
          .delete()
          .eq('comment_id', commentId)
          .eq('user_id', user.id);

        if (error) throw error;
        
        setLikedComments(prev => ({ ...prev, [commentId]: false }));
      } else {
        // Like
        const { error } = await supabase
          .from('comment_likes')
          .insert({
            comment_id: commentId,
            user_id: user.id
          });

        if (error) throw error;
        
        setLikedComments(prev => ({ ...prev, [commentId]: true }));
      }
      
      fetchComments(); // Refresh to get updated like counts
    } catch (error) {
      console.error('Error liking comment:', error);
      alert('Failed to like comment. Please try again.');
    }
  };

  const handlePinComment = async (commentId) => {
    if (!user) return;

    try {
      // Check if user is the blog author
      const { data: post } = await supabase
        .from('blog_posts')
        .select('author_id')
        .eq('id', postId)
        .single();

      if (!post || post.author_id !== user.id) {
        alert('Only the blog author can pin comments.');
        return;
      }

      const { error } = await supabase
        .from('blog_posts')
        .update({ pinned_comment_id: commentId })
        .eq('id', postId);

      if (error) throw error;
      
      setPinnedComment(commentId);
      alert('Comment pinned successfully!');
    } catch (error) {
      console.error('Error pinning comment:', error);
      alert('Failed to pin comment. Please try again.');
    }
  };

  const handleUnpinComment = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ pinned_comment_id: null })
        .eq('id', postId);

      if (error) throw error;
      
      setPinnedComment(null);
      alert('Comment unpinned successfully!');
    } catch (error) {
      console.error('Error unpinning comment:', error);
      alert('Failed to unpin comment. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const CommentItem = ({ comment, isReply = false }) => {
    const canEdit = user && user.id === comment.user_id;
    const canDelete = user && (user.id === comment.user_id || user.role === 'admin');
    const isLiked = likedComments[comment.id];
    const likeCount = comment.likes?.[0]?.count || 0;
    const isPinned = pinnedComment === comment.id;

    return (
      <div className={`comment-item ${isReply ? 'ms-4 border-start ps-3' : 'mb-3'} ${isPinned ? 'border-warning border-2' : ''}`}>
        {isPinned && (
          <div className="mb-2">
            <span className="badge bg-warning text-dark">
              <i className="bi bi-pin-angle-fill me-1"></i>Pinned Comment
            </span>
          </div>
        )}
        <div className="d-flex align-items-start gap-2">
          {comment.user?.profile_image_url ? (
            <img 
              src={comment.user.profile_image_url} 
              alt="avatar" 
              className="rounded-circle"
              style={{ width: 32, height: 32, objectFit: 'cover' }}
            />
          ) : (
            <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
              <i className="bi bi-person text-white"></i>
            </div>
          )}
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-bold">{comment.user?.full_name || 'Anonymous'}</span>
              <small className="text-muted">{formatDate(comment.created_at)}</small>
              {comment.updated_at !== comment.created_at && (
                <small className="text-muted">(edited)</small>
              )}
            </div>
            
            {editingComment === comment.id ? (
              <div className="mb-2">
                <textarea
                  className="form-control"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows="3"
                  style={{ direction: 'ltr', textAlign: 'left' }}
                  dir="ltr"
                />
                <div className="mt-2">
                  <button 
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => handleEditComment(comment.id)}
                  >
                    Save
                  </button>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditingComment(null);
                      setEditText('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-2">
                <p className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</p>
                <div className="d-flex gap-2 align-items-center">
                  {/* Like button */}
                  <button 
                    className={`btn btn-sm ${isLiked ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => handleLikeComment(comment.id)}
                    title={isLiked ? 'Unlike' : 'Like'}
                  >
                    <i className={`bi ${isLiked ? 'bi-heart-fill' : 'bi-heart'}`}></i>
                    <span className="ms-1">{likeCount}</span>
                  </button>

                  {/* Pin button (only for blog author) */}
                  {user && !isReply && (
                    <button 
                      className={`btn btn-sm ${isPinned ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => isPinned ? handleUnpinComment() : handlePinComment(comment.id)}
                      title={isPinned ? 'Unpin comment' : 'Pin comment'}
                    >
                      <i className={`bi ${isPinned ? 'bi-pin-angle-fill' : 'bi-pin-angle'}`}></i>
                    </button>
                  )}

                  {!isReply && (
                    <button 
                      className="btn btn-sm btn-link p-0 text-decoration-none"
                      onClick={() => setReplyingTo(comment.id)}
                    >
                      Reply
                    </button>
                  )}
                  {canEdit && (
                    <button 
                      className="btn btn-sm btn-link p-0 text-decoration-none"
                      onClick={() => {
                        setEditingComment(comment.id);
                        setEditText(comment.content);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button 
                      className="btn btn-sm btn-link p-0 text-decoration-none text-danger"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Reply form */}
            {replyingTo === comment.id && !isReply && (
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Write a reply..."
                  value={replyTexts[comment.id] || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Typing:', newValue);
                    setReplyTexts(prev => {
                      const updated = { ...prev };
                      updated[comment.id] = newValue;
                      return updated;
                    });
                  }}
                  autoFocus
                  key={`reply-input-${comment.id}-${Date.now()}`}
                  style={{ 
                    direction: 'ltr', 
                    textAlign: 'left',
                    unicodeBidi: 'embed',
                    writingMode: 'horizontal-tb'
                  }}
                />
                <div className="mt-2">
                  <button 
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => handleSubmitReply(comment.id)}
                  >
                    Reply
                  </button>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyTexts(prev => {
                        const newState = { ...prev };
                        delete newState[comment.id];
                        return newState;
                      });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Replies */}
            {comment.replies && comment.replies.length > 0 && (
              <div className="mt-2">
                {comment.replies.map(reply => (
                  <CommentItem key={reply.id} comment={reply} isReply={true} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-3">Loading comments...</div>;
  }

  return (
    <div className="comments-section">
      <h4 className="mb-3">
        Comments ({comments.length})
      </h4>

      {/* Comment form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="mb-4">
          <div className="mb-3">
            <textarea
              className="form-control"
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows="3"
              required
              style={{ direction: 'ltr', textAlign: 'left' }}
              dir="ltr"
            />
          </div>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || !newComment.trim()}
          >
            {submitting ? 'Posting...' : 'Post Comment'}
          </button>
        </form>
      ) : (
        <div className="alert alert-info mb-4">
          Please <a href="/login">login</a> to leave a comment.
        </div>
      )}

      {/* Comments list */}
      {comments.length === 0 ? (
        <div className="text-center text-muted py-4">
          No comments yet. Be the first to comment!
        </div>
      ) : (
        <div className="comments-list">
          {comments.map(comment => (
            <CommentItem key={comment.id} comment={comment} />
          ))}
        </div>
      )}
    </div>
  );
}

export default Comments; 